import { useState, useEffect, useCallback } from "react";
import { Shield, LogOut, Save, Loader2, FileText, Newspaper, KeyRound, Search, Users, History } from "lucide-react";
import { translations as staticTranslations } from "../i18n/translations";
import { api, getToken, setToken, clearToken } from "../lib/api";
import { ContentNode } from "./editor/ContentFields";
import NewsManager from "./editor/NewsManager";
import AccountPanel from "./editor/AccountPanel";
import UsersPanel from "./editor/UsersPanel";
import HistoryModal from "./editor/HistoryModal";

const LANGS = [
  { code: "en", label: "English" },
  { code: "al", label: "Shqip" },
  { code: "it", label: "Italiano" },
  { code: "es", label: "Espanol" },
];

// ---- immutable helpers ----
const clone = (o) => JSON.parse(JSON.stringify(o));

function setByPath(root, path, value) {
  const next = Array.isArray(root) ? [...root] : { ...root };
  let node = next;
  for (let i = 0; i < path.length - 1; i++) {
    const k = path[i];
    node[k] = Array.isArray(node[k]) ? [...node[k]] : { ...node[k] };
    node = node[k];
  }
  node[path[path.length - 1]] = value;
  return next;
}

function mergeDeep(base, override) {
  if (override === undefined || override === null) return base;
  if (Array.isArray(base)) return Array.isArray(override) ? override : base;
  if (base && typeof base === "object") {
    const out = { ...base };
    for (const k of Object.keys(override || {})) {
      out[k] = k in base ? mergeDeep(base[k], override[k]) : override[k];
    }
    return out;
  }
  return override;
}

// ---------------- shared shell ----------------
function AuthCard({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: "#050814" }}>
      <div className="w-full max-w-sm cyber-card p-8">
        <div className="flex items-center gap-2 justify-center mb-6">
          <Shield size={22} className="text-[#00F0FF]" />
          <span className="text-lg font-black tracking-tighter text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            FRONT<span className="text-[#00F0FF]">SPACE</span>
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputCls = "w-full mb-4 bg-[#0A0F2E] border border-white/10 focus:border-[#00F0FF]/50 rounded px-3 py-2 text-sm text-slate-100 outline-none";
const labelCls = "block text-[11px] uppercase tracking-wide text-slate-500 mb-1";

// ---------------- Login ----------------
function Login({ onLogin }) {
  const [mode, setMode] = useState("login"); // login | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setInfo(""); setBusy(true);
    try {
      const res = await api.login(email, password);
      setToken(res.token);
      onLogin(res);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setErr(""); setInfo(""); setBusy(true);
    try {
      const res = await api.forgotPassword(email);
      if (res.email_configured === false) {
        setInfo("Password reset by email isn't enabled yet. Ask another admin to reset your password from the Users tab.");
      } else {
        setInfo("If that email is registered, a reset link has been sent. Check your inbox.");
      }
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  if (mode === "forgot") {
    return (
      <AuthCard>
        <h1 className="text-center text-white text-xl font-bold mb-1">Reset password</h1>
        <p className="text-center text-slate-500 text-xs mb-6">Enter your email to receive a reset link</p>
        <form onSubmit={sendReset}>
          <label className={labelCls}>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
          {err && <p className="text-red-400 text-xs mb-4">{err}</p>}
          {info && <p className="text-green-400 text-xs mb-4">{info}</p>}
          <button type="submit" disabled={busy} className="cyber-btn-primary w-full py-2.5 flex items-center justify-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Send reset link"}
          </button>
        </form>
        <button onClick={() => { setMode("login"); setErr(""); setInfo(""); }} className="w-full text-center text-slate-400 hover:text-[#00F0FF] text-xs mt-4">
          ← Back to sign in
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <h1 className="text-center text-white text-xl font-bold mb-1">Content Editor</h1>
      <p className="text-center text-slate-500 text-xs mb-6">Sign in to manage the website</p>
      <form onSubmit={submit}>
        <label className={labelCls}>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
        <label className={labelCls}>Password</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className={inputCls} />
        {err && <p className="text-red-400 text-xs mb-4">{err}</p>}
        <button type="submit" disabled={busy} className="cyber-btn-primary w-full py-2.5 flex items-center justify-center gap-2">
          {busy ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
        </button>
      </form>
      <button onClick={() => { setMode("forgot"); setErr(""); setInfo(""); }} className="w-full text-center text-slate-400 hover:text-[#00F0FF] text-xs mt-4">
        Forgot password?
      </button>
    </AuthCard>
  );
}

// ---------------- Reset via emailed token ----------------
function ResetView({ token, onDone }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.resetPassword(token, pw);
      setOk(true);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard>
      <h1 className="text-center text-white text-xl font-bold mb-1">Set a new password</h1>
      {ok ? (
        <>
          <p className="text-center text-green-400 text-sm my-6">Your password has been updated.</p>
          <button onClick={onDone} className="cyber-btn-primary w-full py-2.5">Go to sign in</button>
        </>
      ) : (
        <>
          <p className="text-center text-slate-500 text-xs mb-6">Choose a new password for your account</p>
          <form onSubmit={submit}>
            <label className={labelCls}>New password</label>
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required className={inputCls} />
            <label className={labelCls}>Confirm new password</label>
            <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required className={inputCls} />
            {err && <p className="text-red-400 text-xs mb-4">{err}</p>}
            <button type="submit" disabled={busy} className="cyber-btn-primary w-full py-2.5 flex items-center justify-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Update password"}
            </button>
          </form>
        </>
      )}
    </AuthCard>
  );
}

// ---------------- Editor shell ----------------
export default function Editor() {
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("content");
  const [resetToken, setResetToken] = useState(() => new URLSearchParams(window.location.search).get("reset"));

  // content editor state
  const [writeLang, setWriteLang] = useState("en");
  const [overrides, setOverrides] = useState({});
  const [tree, setTree] = useState(null);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  // validate existing session on mount
  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    api.me().then(setMe).catch(() => clearToken()).finally(() => setChecking(false));
  }, []);

  const loadContent = useCallback(async () => {
    const data = await api.getContent().catch(() => ({}));
    setOverrides(data || {});
  }, []);

  useEffect(() => { if (me) loadContent(); }, [me, loadContent]);

  // rebuild the editable tree whenever language or overrides change
  useEffect(() => {
    const base = clone(staticTranslations[writeLang] || staticTranslations.en);
    const merged = mergeDeep(base, overrides[writeLang] || {});
    setTree(merged);
    setSaveMsg("");
  }, [writeLang, overrides]);

  const onFieldChange = (path, value) => setTree((prev) => setByPath(prev, path, value));

  const save = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      const res = await api.saveContent(writeLang, tree);
      // refresh overrides so other languages reflect new translations
      await loadContent();
      if (res.changed === 0) {
        setSaveMsg("Saved — no text changes detected.");
      } else if (res.translation_enabled) {
        setSaveMsg(`Saved. ${res.changed} field(s) updated and auto-translated to the other languages.`);
      } else {
        setSaveMsg(`Saved ${res.changed} field(s). Note: translation API key not configured — other languages were left unchanged.`);
      }
    } catch (e) {
      setSaveMsg("Error: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const logout = () => { clearToken(); setMe(null); };

  // Emailed reset link takes priority over any existing session.
  if (resetToken) {
    return <ResetView token={resetToken} onDone={() => { window.history.replaceState({}, "", "/editor"); setResetToken(null); clearToken(); setMe(null); }} />;
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050814" }}>
        <Loader2 className="animate-spin text-[#00F0FF]" size={28} />
      </div>
    );
  }

  if (!me) return <Login onLogin={setMe} />;

  const TABS = [
    { id: "content", label: "Site Content", icon: FileText },
    { id: "news", label: "News", icon: Newspaper },
    { id: "users", label: "Users", icon: Users },
    { id: "account", label: "Account", icon: KeyRound },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050814" }}>
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-[#050814]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-[#00F0FF]" />
            <span className="text-white font-black tracking-tighter" style={{ fontFamily: "Outfit, sans-serif" }}>
              FRONT<span className="text-[#00F0FF]">SPACE</span> <span className="text-slate-500 font-normal text-xs">Editor</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-slate-500 text-xs hidden sm:inline">{me.email}</span>
            <button onClick={logout} className="flex items-center gap-1.5 text-slate-400 hover:text-red-400 text-xs">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-5xl mx-auto px-4 flex gap-1">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm border-b-2 transition-colors ${
                tab === tb.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-transparent text-slate-400 hover:text-white"
              }`}
            >
              <tb.icon size={14} /> {tb.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {tab === "content" && (
          <div>
            {/* Controls */}
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Writing language:</span>
                <div className="flex rounded-md overflow-hidden border border-white/10">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => setWriteLang(l.code)}
                      className={`px-3 py-1.5 text-xs ${writeLang === l.code ? "bg-[#00F0FF] text-[#050814] font-semibold" : "bg-[#0A0F2E] text-slate-300"}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="relative flex-1 min-w-[180px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Search text..."
                  className="w-full bg-[#0A0F2E] border border-white/10 rounded pl-9 pr-3 py-1.5 text-sm text-slate-100 outline-none focus:border-[#00F0FF]/50"
                />
              </div>
              <button
                onClick={() => setShowHistory(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/10 rounded text-slate-300 hover:text-[#00F0FF] hover:border-[#00F0FF]/30"
              >
                <History size={14} /> Version history
              </button>
            </div>

            <div className="mb-4 text-xs text-slate-500 bg-[#0A0F2E] border border-white/10 rounded p-3">
              Edit the text below in <b className="text-slate-300">{LANGS.find((l) => l.code === writeLang)?.label}</b>.
              When you save, changed text is automatically translated to the other languages.
            </div>

            {tree &&
              Object.entries(tree).map(([section, node]) => (
                <ContentNode
                  key={section}
                  node={node}
                  path={[section]}
                  label={section.charAt(0).toUpperCase() + section.slice(1)}
                  filter={filter}
                  onChange={onFieldChange}
                  topLevel
                />
              ))}

            {/* Save bar */}
            <div className="sticky bottom-0 mt-6 -mx-4 px-4 py-3 bg-[#050814]/95 backdrop-blur border-t border-white/10 flex items-center gap-4">
              <button onClick={save} disabled={saving} className="cyber-btn-primary py-2.5 px-6 flex items-center gap-2">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {saving ? "Saving & translating..." : "Save changes"}
              </button>
              {saveMsg && <span className="text-xs text-slate-400">{saveMsg}</span>}
            </div>
          </div>
        )}

        {tab === "news" && <NewsManager langs={LANGS} />}
        {tab === "users" && <UsersPanel me={me} />}
        {tab === "account" && <AccountPanel me={me} onChanged={() => api.me().then(setMe)} />}
      </div>

      {showHistory && (
        <HistoryModal
          onClose={() => setShowHistory(false)}
          onRestored={async () => { await loadContent(); setShowHistory(false); setSaveMsg("Content restored from a previous version."); }}
        />
      )}
    </div>
  );
}
